import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { HoverSegment, Notebook, Page, Stroke, UUID } from '../model/types';

export interface JournalEntry {
  /** Same id the stroke will get when finalized. */
  id: UUID;
  stroke: Omit<Stroke, 'rev'>;
  savedAt: number;
}

export interface SettingRow {
  key: string;
  value: unknown;
}

export interface InkSchema extends DBSchema {
  notebooks: { key: string; value: Notebook; indexes: { rev: number } };
  pages: { key: string; value: Page; indexes: { rev: number; notebookId: string } };
  strokes: { key: string; value: Stroke; indexes: { rev: number; pageId: string; notebookId: string } };
  hover: { key: string; value: HoverSegment; indexes: { rev: number; pageId: string; notebookId: string } };
  /** In-progress stroke checkpoints. Local only, not synced. */
  journal: { key: string; value: JournalEntry };
  /** Local key/value settings, including the rev counter. Not synced. */
  settings: { key: string; value: SettingRow };
}

export type InkDb = IDBPDatabase<InkSchema>;
export type RecordStore = 'notebooks' | 'pages' | 'strokes' | 'hover';
export const RECORD_STORES: readonly RecordStore[] = ['notebooks', 'pages', 'strokes', 'hover'];

export const DB_VERSION = 1;

export function openInkDb(name = 'inkbook'): Promise<InkDb> {
  return openDB<InkSchema>(name, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore('notebooks', { keyPath: 'id' }).createIndex('rev', 'rev');
        const pages = db.createObjectStore('pages', { keyPath: 'id' });
        pages.createIndex('rev', 'rev');
        pages.createIndex('notebookId', 'notebookId');
        for (const name of ['strokes', 'hover'] as const) {
          const s = db.createObjectStore(name, { keyPath: 'id' });
          s.createIndex('rev', 'rev');
          s.createIndex('pageId', 'pageId');
          s.createIndex('notebookId', 'notebookId');
        }
        db.createObjectStore('journal', { keyPath: 'id' });
        db.createObjectStore('settings', { keyPath: 'key' });
      }
    },
  });
}
